package com.routexk.quiz.pages

import com.routexk.quiz.ui.utils.rpx

import androidx.compose.runtime.*
import com.routexk.quiz.analytics.FbPixel
import com.routexk.quiz.analytics.Ga4
import com.routexk.quiz.di.LocalAppContainer
import com.routexk.quiz.funnel.FunnelApi
import com.routexk.quiz.funnel.FunnelCopy
import com.routexk.quiz.funnel.FunnelStore
import com.routexk.quiz.funnel.asMoney
import com.routexk.quiz.funnel.StripeJs
import com.routexk.quiz.funnel.ui.FunnelCard
import com.routexk.quiz.funnel.ui.FunnelCta
import com.routexk.quiz.funnel.ui.FunnelLayout
import com.routexk.quiz.funnel.ui.GuaranteeLine
import com.routexk.quiz.funnel.ui.MoneyCard
import com.routexk.quiz.funnel.ui.PotentialBars
import com.routexk.quiz.funnel.ui.PotentialItem
import com.routexk.quiz.funnel.ui.StickyCtaBar
import com.routexk.quiz.funnel.ui.TestimonialCard
import com.routexk.quiz.funnel.ui.TimerBanner
import com.routexk.quiz.funnel.ui.formatUsdCents
import com.routexk.quiz.funnel.ui.rememberCountdown
import com.routexk.quiz.ui.components.widgets.LoadingSpinner
import com.routexk.quiz.ui.theme.AppColors
import com.routexk.quiz.ui.theme.AppTypography
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

@Page("/checkout")
@Composable
fun CheckoutPage() {
    val ctx = rememberPageContext()
    val container = LocalAppContainer.current
    val scope = rememberCoroutineScope()

    val plan = remember { FunnelStore.selectedPlan }
    val estimate = remember { FunnelStore.estimate }
    val mobile = com.routexk.quiz.ui.utils.isMobile()

    LaunchedEffect(Unit) {
        if (plan == null) ctx.router.navigateTo("/plan")
    }
    if (plan == null) return

    val deadline = remember {
        FunnelStore.discountDeadline ?: (kotlin.js.Date.now() + 10 * 60 * 1000.0)
    }
    val secondsLeft by rememberCountdown(deadline)

    var stripe by remember { mutableStateOf<dynamic>(null) }
    var elements by remember { mutableStateOf<dynamic>(null) }
    var initError by remember { mutableStateOf<String?>(null) }
    var initializing by remember { mutableStateOf(true) }
    var paying by remember { mutableStateOf(false) }
    // True once the Express Checkout Element reports an available wallet
    // (Apple Pay / Google Pay / Link) on this browser/device.
    var walletsReady by remember { mutableStateOf(false) }
    // Surfaced when a wallet redirect returns unpaid (Success bounces here)
    var payError by remember { mutableStateOf(if (ctx.route.params["payment"] == "failed") FunnelCopy.PAYMENT_ERROR else null) }

    val gaPlanId = Ga4.planId(plan.trialDays)
    val planValue = plan.amountCents / 100.0
    var failCount by remember { mutableStateOf(0) }

    val pay: (String) -> Unit = pay@{ paymentMethod ->
        val stripeInstance = stripe ?: return@pay
        val elementsInstance = elements ?: return@pay
        paying = true
        payError = null
        // Survives wallet redirects (full page reload) for the purchase event
        FunnelStore.lastPaymentMethod = paymentMethod
        Ga4.event(
            "add_payment_info",
            "currency" to "USD",
            "value" to planValue,
            "payment_type" to paymentMethod,
            "plan_id" to gaPlanId,
        )
        FbPixel.track(
            "AddPaymentInfo",
            params = arrayOf(
                "content_ids" to FbPixel.ids(gaPlanId),
                "value" to planValue,
                "currency" to "USD",
            ),
        )
        StripeJs.confirmPayment(
            stripe = stripeInstance,
            elements = elementsInstance,
            returnUrl = window.location.origin + "/success"
        ) { errorMessage, transactionId ->
            if (errorMessage != null) {
                failCount++
                payError = errorMessage
                paying = false
                Ga4.event(
                    "payment_failed",
                    "plan_id" to gaPlanId,
                    "value" to planValue,
                    "failure_reason" to Ga4.failureReason(errorMessage),
                )
                FbPixel.trackCustom(
                    "PaymentFailed",
                    "plan_id" to gaPlanId,
                    "failure_reason" to Ga4.failureReason(errorMessage),
                )
                // Bring the card form (with Stripe's inline field errors) into
                // view — the sticky CTA sits far below it on a scrolled page
                kotlinx.browser.document.getElementById("payment-element")
                    ?.scrollIntoView(js("({ behavior: 'smooth', block: 'center' })"))
            } else {
                FunnelStore.paid = true
                FunnelStore.lastTransactionId = transactionId
                Ga4.event(
                    "purchase",
                    "transaction_id" to transactionId,
                    "value" to planValue,
                    "currency" to "USD",
                    "plan_id" to gaPlanId,
                    "payment_method" to paymentMethod,
                    "coupon" to Ga4.COUPON,
                    "items" to Ga4.itemsArray(listOf(Ga4.planItem(gaPlanId, plan.label, plan.amountCents))),
                )
                scope.launch {
                    FunnelApi.trackEvent(container.defaultHttpClient, "purchase_completed")
                }
                ctx.router.navigateTo("/success")
            }
        }
    }

    LaunchedEffect(Unit) {
        Ga4.screenView("checkout", 8, "plan_id" to gaPlanId)
        FbPixel.pageView()
        FbPixel.trackCustom("CheckoutViewed", "plan_id" to gaPlanId)
        // Refresh _fbp/_fbc in the session right before payment — the webhook
        // needs them for CAPI Purchase match quality (wallet redirects land
        // back with a fresh page, so this is the last reliable moment).
        scope.launch {
            FunnelApi.saveFbData(container.defaultHttpClient, "fbp" to FbPixel.fbp(), "fbc" to FbPixel.fbc())
        }
        // Hand GA ids to the backend session so the Stripe webhook can send
        // the server-side purchase to the same GA4 user/session
        Ga4.withClientIds { clientId, gaSessionId ->
            if (clientId != null) scope.launch {
                FunnelApi.saveGaIds(container.defaultHttpClient, clientId, gaSessionId)
            }
        }
        FunnelApi.trackEvent(container.defaultHttpClient, "checkout_view")
        val result = FunnelApi.createIntent(container.defaultHttpClient, plan.uuid)
        if (result.errorCode == "already_subscribed") {
            // This session already holds an active subscription — don't show
            // "payment failed", send them to the plan-active screen instead.
            FunnelStore.paid = true
            ctx.router.navigateTo("/success")
            return@LaunchedEffect
        }
        val init = result.init
        if (init == null || !StripeJs.isLoaded()) {
            initError = FunnelCopy.PAYMENT_ERROR
            initializing = false
            return@LaunchedEffect
        }
        val stripeInstance = StripeJs.create(init.publishableKey)
        val elementsInstance = StripeJs.createElements(stripeInstance, init.clientSecret)
        stripe = stripeInstance
        elements = elementsInstance
        initializing = false
        StripeJs.mountExpressCheckout(
            elements = elementsInstance,
            containerId = "express-checkout-element",
            onReady = { walletsReady = it },
            onClick = { walletType ->
                Ga4.event("payment_method_clicked", "payment_method" to walletType)
            },
            onConfirm = pay
        )
        StripeJs.mountPaymentElement(elementsInstance, "payment-element")
        Ga4.event(
            "begin_checkout",
            "currency" to "USD",
            "value" to planValue,
            "coupon" to Ga4.COUPON,
            "items" to Ga4.itemsArray(listOf(Ga4.planItem(gaPlanId, plan.label, plan.amountCents))),
        )
    }

    FunnelLayout(title = com.routexk.quiz.i18n.t("RouteXK — Checkout", "RouteXK — Pago"), maxContentWidth = 640.rpx, fillViewport = true) {
        Column(
            Modifier.fillMaxWidth().gap(20.rpx),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TimerBanner(secondsLeft)

            // Way back to the paywall so the user can pick a different plan
            Row(
                Modifier
                    .fillMaxWidth()
                    .gap(6.rpx)
                    .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                    .onClick {
                        Ga4.event("checkout_back_to_plans", "plan_id" to gaPlanId)
                        ctx.router.navigateTo("/plan")
                    },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText("←", Modifier.fontSize(16.rpx).color(AppColors.Light.TextSecondary))
                SpanText(
                    FunnelCopy.CHANGE_PLAN,
                    Modifier.fontSize(14.rpx).fontWeight(FontWeight.Medium)
                        .color(AppColors.Light.TextSecondary)
                        .styleModifier { property("text-decoration", "underline") }
                )
            }

            FunnelCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(16.rpx)) {
                    SpanText(
                        FunnelCopy.EXPRESS_CHECKOUT,
                        Modifier
                            .fontSize(20.rpx)
                            .fontWeight(FontWeight.Bold)
                            .fontFamily(AppTypography.SECONDARY_FONT_NAME, AppTypography.SECONDARY_FONT_FALLBACK)
                    )

                    if (initializing) {
                        LoadingSpinner()
                    }

                    initError?.let {
                        SpanText(it, Modifier.fontSize(14.rpx).color(AppColors.Error))
                    }

                    // One-click Apple Pay / Google Pay buttons (Express Checkout Element)
                    Div({ id("express-checkout-element"); style { width(100.percent) } })

                    if (walletsReady) {
                        Row(
                            Modifier.fillMaxWidth().gap(12.rpx),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Box(Modifier.flexGrow(1).height(1.rpx).backgroundColor(AppColors.Light.Divider))
                            SpanText(
                                FunnelCopy.OR_PAY_WITH_CARD,
                                Modifier.fontSize(12.rpx).color(AppColors.Light.TextSecondary)
                            )
                            Box(Modifier.flexGrow(1).height(1.rpx).backgroundColor(AppColors.Light.Divider))
                        }
                    }

                    // Stripe Payment Element renders the card form here
                    Div({ id("payment-element"); style { width(100.percent) } })

                    Column(Modifier.fillMaxWidth().gap(8.rpx).margin(top = 8.rpx)) {
                        SummaryRow(FunnelCopy.yourPlan(plan.label), formatUsdCents(plan.originalCents))
                        SummaryRow(
                            FunnelCopy.DISCOUNT_LINE,
                            "-" + formatUsdCents(plan.originalCents - plan.amountCents),
                            valueColor = AppColors.Primary
                        )
                        Row(
                            Modifier
                                .fillMaxWidth()
                                .padding(top = 8.rpx)
                                .borderTop(1.rpx, LineStyle.Solid, AppColors.Light.Divider),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            SpanText(FunnelCopy.TOTAL, Modifier.fontSize(16.rpx).fontWeight(FontWeight.Bold))
                            SpanText(
                                formatUsdCents(plan.amountCents),
                                Modifier.fontSize(16.rpx).fontWeight(FontWeight.Bold)
                            )
                        }
                    }

                    // Big bold guarantee — the key reassurance right at the point of payment
                    GuaranteeLine(Modifier.fillMaxWidth().margin(top = 4.rpx), emphasized = true)
                }
            }

            payError?.let {
                SpanText(it, Modifier.fontSize(14.rpx).color(AppColors.Error))
            }

            // Design: estimate card, potential bars and testimonials below checkout
            MoneyCard(
                title = FunnelCopy.ESTIMATE_CARD_TITLE,
                value = "+ " + estimate.moneyIncreaseYearUsd.asMoney(),
                pill = FunnelCopy.REPORT_MONEY_PILL,
                modifier = Modifier.fillMaxWidth()
            )
            PotentialBars(
                items = listOf(
                    PotentialItem(FunnelCopy.POTENTIAL_TIME, FunnelCopy.POTENTIAL_HIGH, estimate.timeSavingPotentialPct, "/funnel/clock-icon.svg"),
                    PotentialItem(FunnelCopy.POTENTIAL_FUEL, FunnelCopy.POTENTIAL_MEDIUM, estimate.fuelSavingPotentialPct, "/funnel/fuelpump-icon.svg"),
                    PotentialItem(
                        if (mobile) FunnelCopy.POTENTIAL_MONEY_SHORT else FunnelCopy.POTENTIAL_MONEY,
                        FunnelCopy.POTENTIAL_EXTREMELY_HIGH,
                        estimate.moneyIncreasePotentialPct,
                        "/funnel/dollar-icon.svg"
                    ),
                ),
                modifier = Modifier.fillMaxWidth(),
                compact = mobile
            )
            Column(Modifier.fillMaxWidth().gap(14.rpx)) {
                FunnelCopy.TESTIMONIALS.take(3).forEach {
                    TestimonialCard(it, Modifier.fillMaxWidth())
                }
            }
            GuaranteeLine(Modifier.fillMaxWidth())

            // Renewal terms, de-emphasized at the very bottom of the page
            if (plan.trialDays > 0 && plan.renewalCents > 0) {
                SpanText(
                    FunnelCopy.TRIAL_DISCLOSURE
                        .replace("%1\$s", FunnelCopy.trialLengthLabel(plan.trialDays))
                        .replace("%2\$s", formatUsdCents(plan.renewalCents)),
                    Modifier
                        .fillMaxWidth()
                        .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Center)
                        .fontSize(12.rpx).lineHeight(16.rpx)
                        .color(AppColors.Light.TextSecondary)
                )
            }

            // Sticky CTA: always visible at the bottom regardless of scroll
            StickyCtaBar {
                FunnelCta(
                    text = FunnelCopy.CHECKOUT_CTA,
                    enabled = stripe != null && !paying,
                    loading = paying,
                    onClick = {
                        Ga4.event("payment_method_clicked", "payment_method" to "card")
                        if (failCount > 0) {
                            Ga4.event(
                                "retry_payment_clicked",
                                "plan_id" to gaPlanId,
                                "attempt_number" to failCount + 1,
                            )
                        }
                        pay("card")
                    }
                )
            }
        }
    }
}

@Composable
private fun SummaryRow(
    label: String,
    value: String,
    valueColor: com.varabyte.kobweb.compose.ui.graphics.Color = AppColors.Light.TextPrimary
) {
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween
    ) {
        SpanText(label, Modifier.fontSize(14.rpx).color(AppColors.Light.TextSecondary))
        SpanText(value, Modifier.fontSize(14.rpx).fontWeight(FontWeight.SemiBold).color(valueColor))
    }
}
